<template>
  <div class="invitation-list">
    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stats-row">
      <el-col :span="4" v-for="(item, key) in stats" :key="key">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value">{{ item }}</div>
          <div class="stat-label">{{ getStatLabel(key) }}</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 标签页切换 -->
    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <el-tab-pane label="全部邀请" name="all" />
      <el-tab-pane name="pending-review">
        <template #label>
          <span>待审核</span>
          <el-badge v-if="stats.pendingReview > 0" :value="stats.pendingReview" class="tab-badge" />
        </template>
      </el-tab-pane>
    </el-tabs>

    <!-- 筛选条件 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="queryParams" class="filter-form">
        <el-form-item label="邀请类型" v-show="activeTab === 'all'">
          <el-select v-model="queryParams.type" placeholder="全部" clearable>
            <el-option label="邀请债务人" value="debtor" />
            <el-option label="邀请债权人" value="creditor" />
            <el-option label="邀请亲友团" value="friend" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" v-show="activeTab === 'all'">
          <el-select v-model="queryParams.status" placeholder="全部" clearable>
            <el-option label="待处理" value="pending" />
            <el-option label="待审核" value="pending_review" />
            <el-option label="已接受" value="accepted" />
            <el-option label="已拒绝" value="rejected" />
            <el-option label="已过期" value="expired" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-form-item>
        <el-form-item label="搜索">
          <el-input v-model="queryParams.keyword" placeholder="手机号/姓名" clearable />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
      <el-button type="warning" @click="handleExpired" :loading="expiredLoading">
        处理过期邀请
      </el-button>
    </el-card>

    <!-- 表格 -->
    <el-card>
      <el-table :data="tableData" v-loading="loading" stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="类型" width="120">
          <template #default="{ row }">
            <el-tag :type="getTypeTagType(row.type)">{{ getTypeText(row.type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)">{{ getStatusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="审核状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getReviewStatusTagType(row.reviewStatus)">
              {{ getReviewStatusText(row.reviewStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="邀请人" width="150">
          <template #default="{ row }">
            {{ row.inviter?.realName || row.inviter?.mobile || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="被邀请人" width="150">
          <template #default="{ row }">
            {{ row.inviteeName || row.inviteeMobile }}
          </template>
        </el-table-column>
        <el-table-column label="债务金额" width="120">
          <template #default="{ row }">
            <span v-if="row.debtAmount" class="amount">¥{{ row.debtAmount }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="debtType" label="债务类型" width="100">
          <template #default="{ row }">
            {{ row.debtType || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="发起时间" width="180" />
        <el-table-column label="操作" fixed="right" width="200">
          <template #default="{ row }">
            <el-button type="primary" link @click="handleDetail(row)">详情</el-button>
            <template v-if="row.reviewStatus === 'pending'">
              <el-button type="success" link @click="handleApprove(row)">通过</el-button>
              <el-button type="danger" link @click="handleReject(row)">拒绝</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        v-model:current-page="queryParams.page"
        v-model:page-size="queryParams.pageSize"
        :total="total"
        :page-sizes="[10, 20, 50]"
        layout="total, sizes, prev, pager, next"
        @size-change="loadData"
        @current-change="loadData"
        class="pagination"
      />
    </el-card>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailVisible" title="邀请详情" width="600px">
      <el-descriptions :column="2" border v-if="currentDetail">
        <el-descriptions-item label="ID">{{ currentDetail.id }}</el-descriptions-item>
        <el-descriptions-item label="类型">{{ getTypeText(currentDetail.type) }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ getStatusText(currentDetail.status) }}</el-descriptions-item>
        <el-descriptions-item label="审核状态">{{ getReviewStatusText(currentDetail.reviewStatus) }}</el-descriptions-item>
        <el-descriptions-item label="邀请人">{{ currentDetail.inviter?.realName || currentDetail.inviter?.mobile }}</el-descriptions-item>
        <el-descriptions-item label="被邀请人">{{ currentDetail.inviteeName || currentDetail.inviteeMobile }}</el-descriptions-item>
        <el-descriptions-item label="手机号">{{ currentDetail.inviteeMobile }}</el-descriptions-item>
        <el-descriptions-item label="债务金额" v-if="currentDetail.debtAmount">¥{{ currentDetail.debtAmount }}</el-descriptions-item>
        <el-descriptions-item label="债务类型" v-if="currentDetail.debtType">{{ currentDetail.debtType }}</el-descriptions-item>
        <el-descriptions-item label="借款日期">{{ currentDetail.borrowDate || '-' }}</el-descriptions-item>
        <el-descriptions-item label="到期日期">{{ currentDetail.dueDate || '-' }}</el-descriptions-item>
        <el-descriptions-item label="说明" :span="2">{{ currentDetail.description || '-' }}</el-descriptions-item>
        <el-descriptions-item label="发起时间">{{ currentDetail.createdAt }}</el-descriptions-item>
        <el-descriptions-item label="响应时间">{{ currentDetail.respondedAt || '-' }}</el-descriptions-item>
        <el-descriptions-item label="审核时间">{{ currentDetail.reviewedAt || '-' }}</el-descriptions-item>
        <el-descriptions-item label="审核备注">{{ currentDetail.reviewRemark || '-' }}</el-descriptions-item>
        <el-descriptions-item label="拒绝原因" :span="2" v-if="currentDetail.rejectReason">{{ currentDetail.rejectReason }}</el-descriptions-item>
        <el-descriptions-item label="关联合同" v-if="currentDetail.contract">
          {{ currentDetail.contract.contractNo }} - {{ currentDetail.contract.title }}
        </el-descriptions-item>
      </el-descriptions>
      <template #footer v-if="currentDetail && currentDetail.reviewStatus === 'pending'">
        <el-button @click="detailVisible = false">取消</el-button>
        <el-button type="danger" @click="handleReject(currentDetail)">拒绝</el-button>
        <el-button type="success" @click="handleApprove(currentDetail)">通过审核</el-button>
      </template>
    </el-dialog>

    <!-- 审核拒绝弹窗 -->
    <el-dialog v-model="rejectVisible" title="审核拒绝" width="400px">
      <el-form>
        <el-form-item label="拒绝原因">
          <el-input v-model="rejectRemark" type="textarea" :rows="3" placeholder="请输入拒绝原因" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmReject" :loading="rejectLoading">确认拒绝</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { 
  getInvitationList, 
  getInvitationDetail, 
  getInvitationStats, 
  handleExpiredInvitations,
  getPendingReviewList,
  approveInvitation,
  rejectReview
} from '@/api/modules/invitation';

// 数据
const loading = ref(false);
const expiredLoading = ref(false);
const rejectLoading = ref(false);
const tableData = ref([]);
const total = ref(0);
const stats = ref({});
const detailVisible = ref(false);
const rejectVisible = ref(false);
const currentDetail = ref(null);
const currentRejectItem = ref(null);
const rejectRemark = ref('');
const activeTab = ref('all');

const queryParams = reactive({
  page: 1,
  pageSize: 10,
  type: '',
  status: '',
  keyword: ''
});

// 加载数据
const loadData = async () => {
  loading.value = true;
  try {
    let res;
    if (activeTab.value === 'pending-review') {
      res = await getPendingReviewList(queryParams);
    } else {
      res = await getInvitationList(queryParams);
    }
    if (res.code === 200) {
      tableData.value = res.data.list;
      total.value = res.data.total;
    }
  } catch (err) {
    console.error('加载失败:', err);
    ElMessage.error('加载失败');
  } finally {
    loading.value = false;
  }
};

// 加载统计
const loadStats = async () => {
  try {
    const res = await getInvitationStats();
    if (res.code === 200) {
      stats.value = res.data;
    }
  } catch (err) {
    console.error('加载统计失败:', err);
  }
};

// 标签页切换
const handleTabChange = () => {
  queryParams.page = 1;
  loadData();
};

// 搜索
const handleSearch = () => {
  queryParams.page = 1;
  loadData();
};

// 重置
const handleReset = () => {
  queryParams.type = '';
  queryParams.status = '';
  queryParams.keyword = '';
  queryParams.page = 1;
  loadData();
};

// 处理过期
const handleExpired = async () => {
  expiredLoading.value = true;
  try {
    const res = await handleExpiredInvitations();
    if (res.code === 200) {
      ElMessage.success(res.data.message);
      loadData();
      loadStats();
    }
  } catch (err) {
    console.error('处理过期失败:', err);
    ElMessage.error('处理失败');
  } finally {
    expiredLoading.value = false;
  }
};

// 查看详情
const handleDetail = async (row) => {
  try {
    const res = await getInvitationDetail(row.id);
    if (res.code === 200) {
      currentDetail.value = res.data;
      detailVisible.value = true;
    }
  } catch (err) {
    console.error('获取详情失败:', err);
    ElMessage.error('获取详情失败');
  }
};

// 审核通过
const handleApprove = async (row) => {
  try {
    await ElMessageBox.confirm('确定通过此邀请的审核吗？通过后将生成邀请二维码和合同。', '审核通过', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'info'
    });
    
    const res = await approveInvitation(row.id);
    if (res.code === 200) {
      ElMessage.success('审核通过');
      detailVisible.value = false;
      loadData();
      loadStats();
    }
  } catch (err) {
    if (err !== 'cancel') {
      console.error('审核失败:', err);
      ElMessage.error('审核失败');
    }
  }
};

// 审核拒绝
const handleReject = (row) => {
  currentRejectItem.value = row;
  rejectRemark.value = '';
  rejectVisible.value = true;
};

// 确认拒绝
const confirmReject = async () => {
  if (!rejectRemark.value) {
    ElMessage.warning('请输入拒绝原因');
    return;
  }
  
  rejectLoading.value = true;
  try {
    const res = await rejectReview(currentRejectItem.value.id, rejectRemark.value);
    if (res.code === 200) {
      ElMessage.success('已拒绝');
      rejectVisible.value = false;
      detailVisible.value = false;
      loadData();
      loadStats();
    }
  } catch (err) {
    console.error('拒绝失败:', err);
    ElMessage.error('操作失败');
  } finally {
    rejectLoading.value = false;
  }
};

// 辅助方法
const getTypeText = (type) => {
  const map = { debtor: '邀请债务人', creditor: '邀请债权人', friend: '邀请亲友团' };
  return map[type] || type;
};

const getStatusText = (status) => {
  const map = { 
    pending: '待处理', 
    pending_review: '待审核', 
    accepted: '已接受', 
    rejected: '已拒绝', 
    expired: '已过期', 
    cancelled: '已取消' 
  };
  return map[status] || status;
};

const getReviewStatusText = (status) => {
  const map = { pending: '待审核', approved: '已通过', rejected: '已拒绝' };
  return map[status] || '-';
};

const getTypeTagType = (type) => {
  const map = { debtor: 'danger', creditor: 'success', friend: 'warning' };
  return map[type] || 'info';
};

const getStatusTagType = (status) => {
  const map = { 
    pending: 'warning', 
    pending_review: 'warning', 
    accepted: 'success', 
    rejected: 'danger', 
    expired: 'info', 
    cancelled: 'info' 
  };
  return map[status] || 'info';
};

const getReviewStatusTagType = (status) => {
  const map = { pending: 'warning', approved: 'success', rejected: 'danger' };
  return map[status] || 'info';
};

const getStatLabel = (key) => {
  const map = { 
    pending: '待处理', 
    pendingReview: '待审核',
    accepted: '已接受', 
    rejected: '已拒绝', 
    expired: '已过期', 
    cancelled: '已取消', 
    total: '总数', 
    acceptRate: '接受率' 
  };
  return map[key] || key;
};

// 初始化
onMounted(() => {
  loadData();
  loadStats();
});
</script>

<style scoped>
.invitation-list {
  padding: 20px;
}

.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #409eff;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 5px;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-form {
  display: inline-block;
}

.pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

.amount {
  color: #f56c6c;
  font-weight: bold;
}

.tab-badge {
  margin-left: 8px;
}
</style>
